import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import api from '../../utils/api';
import AdminDashboard   from './AdminDashboard';
import AdminInventory   from './AdminInventory';
import AdminOrders      from './AdminOrders';
import AdminCustomers   from './AdminCustomers';
import AdminEmployees   from './AdminEmployees';
import AdminSettings    from './AdminSettings';
import AdminImport      from './AdminImport';
import AdminInquiries   from './AdminInquiries';
import AdminUsers       from './AdminUsers';
import AdminPayments    from './AdminPayments';
import NotificationCenter from '../../components/admin/NotificationCenter';

const NAV = [
  { path: 'dashboard',  label: 'Dashboard',  icon: '📊' },
  { path: 'inventory',  label: 'Inventory',  icon: '📦' },
  { path: 'orders',     label: 'Orders',     icon: '🛒' },
  { path: 'payments',   label: 'Payments',   icon: '💳', roles: ['owner', 'manager'] },
  { path: 'inquiries',  label: 'Inquiries',  icon: '💬' },
  { path: 'customers',  label: 'Customers',  icon: '👥' },
  { path: 'employees',  label: 'Team',       icon: '👤' },
  { path: 'import',     label: 'CSV Import', icon: '📥' },
  { path: 'users',      label: 'Staff Logins', icon: '🔑', ownerOnly: true },
  { path: 'settings',   label: 'Settings',   icon: '⚙️' },
];

export default function AdminShell() {
  const [user, setUser] = useState(null);
  const [collapsed, setCollapsed] = useState(window.innerWidth < 768);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    api.get('/admin/auth/me').then(r => setUser(r.data)).catch(() => navigate('/admin/login'));
  }, [navigate]);

  // Installing from an admin page gives staff a home-screen app that opens to Inquiries (needed for iPhone push)
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const original = link?.getAttribute('href');
    link?.setAttribute('href', '/admin-manifest.json');
    return () => { if (link && original) link.setAttribute('href', original); };
  }, []);

  useEffect(() => {
    const base = document.title.replace(/^\(\d+\) /, '');
    document.title = unread ? `(${unread}) ${base}` : base;
  }, [unread]);

  const logout = () => { localStorage.removeItem('admin_token'); navigate('/admin/login'); };
  const active = (path) => location.pathname.includes(path);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f4f4' }}>
      {/* Sidebar */}
      <aside style={{ width: collapsed ? 60 : 220, background: '#1a2744', color: '#fff', display: 'flex', flexDirection: 'column', flexShrink: 0, transition: 'width 0.2s', overflow: 'hidden' }}>
        {/* Logo */}
        <div style={{ padding: collapsed ? '20px 10px' : '20px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, background: '#c9a227', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 14, color: '#1a2744', flexShrink: 0 }}>PP</div>
          {!collapsed && <div><div style={{ fontWeight: 800, fontSize: 13, lineHeight: 1.2 }}>Premier Pawn</div><div style={{ color: '#c9a227', fontSize: 11, fontWeight: 600 }}>Admin</div></div>}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '12px 0' }}>
          {NAV.filter(({ ownerOnly, roles }) => (!ownerOnly || user?.role === 'owner') && (!roles || roles.includes(user?.role))).map(({ path, label, icon }) => (
            <Link key={path} to={`/admin/${path}`}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: collapsed ? '12px 14px' : '12px 16px', color: active(path) ? '#c9a227' : 'rgba(255,255,255,0.7)', background: active(path) ? 'rgba(201,162,39,0.12)' : 'transparent', fontWeight: active(path) ? 700 : 400, fontSize: 13, borderLeft: active(path) ? '3px solid #c9a227' : '3px solid transparent', transition: 'all 0.15s', textDecoration: 'none', whiteSpace: 'nowrap' }}
              onMouseEnter={e => { if (!active(path)) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
              onMouseLeave={e => { if (!active(path)) e.currentTarget.style.background = 'transparent'; }}>
              <span style={{ fontSize: 16, flexShrink: 0, position: 'relative' }}>
                {icon}
                {path === 'inquiries' && unread > 0 && collapsed && <span style={{ position: 'absolute', top: -4, right: -6, width: 8, height: 8, borderRadius: '50%', background: '#c62828' }} />}
              </span>
              {!collapsed && label}
              {!collapsed && path === 'inquiries' && unread > 0 && <span style={{ marginLeft: 'auto', background: '#c62828', color: '#fff', borderRadius: 10, fontSize: 10, fontWeight: 800, padding: '1px 7px' }}>{unread}</span>}
            </Link>
          ))}
        </nav>

        {/* User + collapse */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', padding: '12px 16px' }}>
          {!collapsed && user && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>{user.full_name} · {user.role}</div>}
          <button onClick={logout} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 12, cursor: 'pointer', padding: 0 }}>Sign Out</button>
        </div>
      </aside>

      {/* Main */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Top bar */}
        <header style={{ background: '#fff', borderBottom: '1px solid #e8e8e8', padding: '0 24px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <button onClick={() => setCollapsed(c => !c)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#666', padding: 4 }}>☰</button>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {user && <NotificationCenter onUnreadChange={setUnread} />}
            <Link to="/" target="_blank" style={{ fontSize: 13, color: '#c9a227', fontWeight: 600 }}>View Site ↗</Link>
            {user && <span style={{ fontSize: 13, color: '#666' }}>👤 {user.full_name}</span>}
          </div>
        </header>

        {/* Page content */}
        <main style={{ flex: 1, overflow: 'auto', padding: 24 }}>
          <Routes>
            <Route path="dashboard"  element={<AdminDashboard />} />
            <Route path="inventory"  element={<AdminInventory />} />
            <Route path="inventory/:id" element={<AdminInventory />} />
            <Route path="orders"     element={<AdminOrders />} />
            <Route path="payments"   element={<AdminPayments />} />
            <Route path="inquiries"  element={<AdminInquiries />} />
            <Route path="customers"  element={<AdminCustomers />} />
            <Route path="employees"  element={<AdminEmployees />} />
            <Route path="import"     element={<AdminImport />} />
            <Route path="users"      element={<AdminUsers />} />
            <Route path="settings"   element={<AdminSettings />} />
            <Route path="*"          element={<AdminDashboard />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
